import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Users, Plus, Lightning } from '@phosphor-icons/react';

const TribeCard = ({ t, onJoin }) => (
  <div data-testid={`tribe-card-${t.id}`} className="glass rounded-2xl p-5 flex flex-col">
    <div className="flex items-center justify-between">
      <div className="chip text-bx-cyan border-bx-cyan/30">{t.tag}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/60">
        {t.member_count} members
      </div>
    </div>
    <h3 className="font-display text-xl font-bold mt-3">{t.name}</h3>
    <p className="text-sm text-white/60 mt-1 flex-1">{t.description || 'No description.'}</p>
    <div className="mt-4 flex items-center justify-between">
      <div className="flex items-center gap-1 text-bx-volt text-sm font-bold">
        <Lightning size={14} weight="fill" /> {t.energy}
      </div>
      <div className="flex gap-2">
        <Link to={`/tribe/${t.id}`} className="text-bx-cyan text-xs font-bold uppercase tracking-widest" data-testid={`tribe-view-${t.id}`}>View →</Link>
        <button data-testid={`tribe-join-${t.id}`} onClick={() => onJoin(t.id)} className="btn-volt px-3 py-1.5 rounded-full text-xs">Join</button>
      </div>
    </div>
  </div>
);

const Tribes = () => {
  const [tribes, setTribes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', tag: 'community' });

  const load = async () => {
    const { data } = await api.get('/tribes');
    setTribes(data.tribes);
  };
  useEffect(() => { load(); }, []);

  const join = async (id) => {
    try { await api.post(`/tribes/${id}/join`); toast.success('Joined the tribe.'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'cannot join'); }
  };

  const create = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post('/tribes', form);
      toast.success('Tribe created');
      setShowCreate(false); setForm({ name: '', description: '', tag: 'community' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'failed'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Tribes</h1>
          <p className="text-white/60 mt-1">Find your people. Compete on the global leaderboard.</p>
        </div>
        <button data-testid="tribes-create-btn" onClick={() => setShowCreate(!showCreate)} className="btn-primary px-5 py-3 rounded-full text-sm flex items-center gap-1.5">
          <Plus size={14} weight="bold" /> Create Tribe
        </button>
      </div>

      {showCreate && (
        <div className="glass rounded-2xl p-5 mb-6 space-y-3">
          <input data-testid="tribe-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tribe name" className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          <input data-testid="tribe-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="tag (city, school, music…)" className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          <textarea data-testid="tribe-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's this tribe about?" rows={2} className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm resize-none" />
          <button data-testid="tribe-create-submit" onClick={create} className="btn-primary px-5 py-2.5 rounded-full text-sm">Create</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {tribes.map((t) => (
          <TribeCard key={t.id} t={t} onJoin={join} />
        ))}
      </div>
    </div>
  );
};

export default Tribes;
